import { useRole } from "@/hooks/userRole";
import type { HistoryVoucherType, VoucherStats } from "@/types";
import { extractErrorMessage } from "@/utilities/errorExtract";
import { Box, Card, CardContent, Skeleton } from "@mui/material";
import { useCallback, useEffect, useState } from "react";
import { Title, useDataProvider, useNotify } from "react-admin";
import type { DataProvider } from "react-admin";
import EntityDashboard from "./EntityDashboard";
import AdminDashboard from "./AdminDashboard";
import CashierDashboard from "./CashierDashboard";

type DashboardDataProvider = DataProvider & {
  getVoucherStats: () => Promise<{ data: VoucherStats }>;
  getHistoryVoucher: () => Promise<{ data: HistoryVoucherType[] }>;
};

const Dashboard = () => {
  const [dataChart, setChartData] = useState<VoucherStats | null>(null);
  const [loading, setLoading] = useState(false);
  const [historyVoucher, setHistoryVoucher] = useState<HistoryVoucherType[]>(
    [],
  );

  const dp = useDataProvider() as DashboardDataProvider;
  const notify = useNotify();
  const { role, loading: roleLoading } = useRole();

  const getStats = useCallback(async () => {
    if (!role) return;

    try {
      setLoading(true);

      if (role !== "cashier") {
        const [resDataChartOverview, resHistoryVoucher] = await Promise.all([
          dp.getVoucherStats(),
          dp.getHistoryVoucher(),
        ]);

        setChartData(resDataChartOverview.data);
        setHistoryVoucher(resHistoryVoucher.data);
      } else {
        const resHistoryVoucher = await dp.getHistoryVoucher();
        setHistoryVoucher(resHistoryVoucher.data);
      }
    } catch (e) {
      console.error("Dashboard getStats error:", e);
      notify(extractErrorMessage(e), { type: "warning" });
    } finally {
      setLoading(false);
    }
  }, [role, dp, notify]);

  useEffect(() => {
    getStats();
  }, [getStats]);

  if (roleLoading) {
    return (
      <Card>
        <CardContent>
          <Skeleton variant="rectangular" height={300} />
        </CardContent>
      </Card>
    );
  }

  if (!role) {
    return (
      <Card>
        <CardContent>
          <Box mb={2}>
            <Skeleton variant="rectangular" height={40} />
          </Box>
          <Skeleton variant="rectangular" height={300} />
        </CardContent>
      </Card>
    );
  }

  if (loading || (role !== "cashier" && !dataChart)) {
    return (
      <Card>
        <CardContent>
          <Skeleton variant="rectangular" height={300} />
        </CardContent>
      </Card>
    );
  }

  return (
    <Card>
      <Title title="Dashboard" />
      <CardContent>
        {role === "admin" && (
          <AdminDashboard
            dataChart={dataChart}
            historyVoucher={historyVoucher}
          />
        )}

        {role === "entity" && (
          <EntityDashboard
            dataChart={dataChart}
            historyVoucher={historyVoucher}
          />
        )}

        {role === "cashier" && (
          <CashierDashboard historyVoucher={historyVoucher} />
        )}
      </CardContent>
    </Card>
  );
};

export default Dashboard;
